import React,{useEffect,useMemo,useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Sidebar from './components/Sidebar';
import CreateScreen from './components/CreateScreen';
import ReviewScreen from './components/ReviewScreen';
import LibraryScreen from './components/LibraryScreen';
import LearningsScreen from './components/LearningsScreen';
import CalendarScreen from './components/CalendarScreen';
import LandingPage from './components/LandingPage';
import AuthScreen from './components/AuthScreen';
import BrandSetup from './components/BrandSetup';
import ConnectionsScreen from './components/ConnectionsScreen';
import FirstRunOnboarding from './components/FirstRunOnboarding';
import AssetLibraryScreen from './components/AssetLibraryScreen';
import TeamScreen from './components/TeamScreen';
import BillingScreen from './components/BillingScreen';
import {useAuth} from './context/AuthContext.jsx';
import * as api from './lib/api.js';
import {
  initialPosts,
  postTemplates
} from './data/mockData';
import {
  dalepostPosts
} from './data/dalepostMockData';
import {getAccountPlan} from './data/accountPlans';
import {selectRandomLayout} from './data/postLayouts';
import {
  createMockEngagement,
  hydratePublishedPost
} from './data/mockEngagement';
import './App.css';

/** Set to true for local dev without Edge Function / API keys. */
const USE_MOCK_GENERATION=false;

const {FiMenu,FiX}=FiIcons;

const fallbackBrand={
  id:'new-workspace',
  name:'Your brand',
  monogram:'D',
  primary:'#19382f',
  accent:'#c99b5a',
  voice:'Clear,thoughtful,and distinctly yours.',
  preferredLayouts:['editorial','bold','bottom']
};

const initialTeamMembers=[
  {
    id:'owner-dale',
    name:'Dale Anderson',
    email:'dale@dalepost.example',
    role:'Founder',
    status:'active'
  }
];

function normalizeBrand(brand) {
  return {
    ...brand,
    tone:brand.tone || {
      formality:3,
      energy:3,
      warmth:3,
      notes:brand.voice || ''
    },
    ctaOptions:brand.ctaOptions ?? [],
    writingStyle:brand.writingStyle || 'Punchy & direct',
    preferredLayouts:brand.preferredLayouts?.length
      ? brand.preferredLayouts
      : ['editorial','bold','bottom'],
    layoutOverrides:brand.layoutOverrides || {}
  };
}

const allInitialPosts=[
  ...initialPosts,
  ...dalepostPosts
].map(hydratePublishedPost);

function getMonogram(name='') {
  const words=name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 'NB';
  return words.slice(0,2).map((word)=> word[0]).join('').toUpperCase();
}

function formatAssetDate(iso) {
  const date=new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Recently added';
  return date.toLocaleDateString(undefined,{
    month:'short',
    day:'numeric',
    year:'numeric'
  });
}

function createEmptyConnections() {
  return {
    instagram:{connected:false,handle:null,id:null},
    facebook:{connected:false,handle:null,id:null},
    linkedin:{connected:false,handle:null,id:null},
    tiktok:{connected:false,handle:null,id:null}
  };
}

function mapConnectionsToLocal(rows) {
  const result=createEmptyConnections();

  for (const row of rows || []) {
    result[row.platform]={
      connected:row.connected,
      handle:row.handle,
      id:row.id
    };
  }

  return result;
}

function mapDbAssetToLocal(row) {
  return {
    id:row.id,
    imageUrl:api.brandAssets.getBrandAssetPublicUrl(row.storage_path),
    label:row.label,
    addedDate:formatAssetDate(row.added_at),
    source:row.source,
    storagePath:row.storage_path
  };
}

function mapLocalBrandToDb(values) {
  const logo=values.logo?.startsWith('blob:')
    || values.logo?.startsWith('data:')
    ? null
    : (values.logo || null);

  return {
    name:values.name,
    logo_url:logo,
    colors:{primary:values.primary,accent:values.accent},
    fonts:{display:values.displayFont,body:values.bodyFont},
    voice:values.voice,
    tone:values.tone,
    cta_options:values.ctaOptions ?? [],
    writing_style:values.writingStyle,
    preferred_layouts:values.preferredLayouts,
    layout_overrides:values.layoutOverrides || {},
    business_summary:values.businessSummary?.trim() || null,
    products_services:values.productsServices?.trim() || null,
    mission:values.mission?.trim() || null,
    vision:values.vision?.trim() || null,
    target_audience:values.targetAudience?.trim() || null,
    strategic_value:values.strategicValue?.trim() || null
  };
}

function mapDbBrandToLocal(dbBrand) {
  return normalizeBrand({
    id:dbBrand.id,
    name:dbBrand.name,
    monogram:getMonogram(dbBrand.name),
    logo:dbBrand.logo_url || '',
    primary:dbBrand.colors?.primary || '#19382f',
    accent:dbBrand.colors?.accent || '#c99b5a',
    displayFont:dbBrand.fonts?.display || 'DM Serif Display',
    bodyFont:dbBrand.fonts?.body || 'DM Sans',
    voice:dbBrand.voice || '',
    tone:dbBrand.tone || {},
    ctaOptions:dbBrand.cta_options ?? [],
    writingStyle:dbBrand.writing_style || 'Punchy & direct',
    preferredLayouts:dbBrand.preferred_layouts?.length
      ? dbBrand.preferred_layouts
      : ['editorial','bold','bottom'],
    layoutOverrides:dbBrand.layout_overrides || {},
    businessSummary:dbBrand.business_summary || '',
    productsServices:dbBrand.products_services || '',
    mission:dbBrand.mission || '',
    vision:dbBrand.vision || '',
    targetAudience:dbBrand.target_audience || '',
    strategicValue:dbBrand.strategic_value || '',
    layoutVocabulary:dbBrand.layout_vocabulary || ''
  });
}

async function syncBrandLogo(accountId,brandId,brandName,logoFile) {
  const {error:removeError}=await api.brandAssets.removeLogoAssets(brandId);

  if (removeError) {
    return {logoUrl:null,error:removeError};
  }

  const {data,error}=await api.brandAssets.uploadBrandLogo(
    accountId,
    brandId,
    logoFile
  );

  if (error) {
    return {logoUrl:null,error};
  }

  const {error:assetError}=await api.brandAssets.addAsset(brandId,{
    storage_path:data.storagePath,
    label:`${brandName} logo`,
    source:'logo'
  });

  if (assetError) {
    return {logoUrl:null,error:assetError};
  }

  return {logoUrl:data.publicUrl,error:null};
}

function App() {
  const {
    currentUser,
    currentAccount,
    currentRole,
    loading:authLoading,
    passwordRecovery,
    needsFirstRunOnboarding,
    clearNeedsFirstRunOnboarding,
    refreshContext,
    isAuthenticated
  }=useAuth();
  const [showLanding,setShowLanding]=useState(true);
  const [brands,setBrands]=useState([]);
  const [brandsLoading,setBrandsLoading]=useState(false);
  const [brandsError,setBrandsError]=useState(null);
  const [brandId,setBrandId]=useState('');
  const [posts,setPosts]=useState(allInitialPosts);
  const [libraryPosts,setLibraryPosts]=useState([]);
  const [libraryPostsLoading,setLibraryPostsLoading]=useState(false);
  const [libraryPostsError,setLibraryPostsError]=useState(null);
  const [createError,setCreateError]=useState(null);
  const [connections,setConnections]=useState({});
  const [connectionsLoading,setConnectionsLoading]=useState(false);
  const [connectionsError,setConnectionsError]=useState(null);
  const [assets,setAssets]=useState({});
  const [assetsLoading,setAssetsLoading]=useState(false);
  const [assetsError,setAssetsError]=useState(null);
  const [teamMembers,setTeamMembers]=useState(initialTeamMembers);
  const [currentPlan,setCurrentPlan]=useState(()=> getAccountPlan('growing'));
  const [selectedBackground,setSelectedBackground]=useState(null);
  const [currentPost,setCurrentPost]=useState(null);
  const [screen,setScreen]=useState('create');
  const [returnScreen,setReturnScreen]=useState('create');
  const [brandSetupMode,setBrandSetupMode]=useState('edit');
  const [setupBrand,setSetupBrand]=useState(null);
  const [setupBrandLoading,setSetupBrandLoading]=useState(false);
  const [generating,setGenerating]=useState(false);
  const [menuOpen,setMenuOpen]=useState(false);
  const [firstRunStage,setFirstRunStage]=useState(null);

  useEffect(()=> {
    if (authLoading || !isAuthenticated || !showLanding) {
      return;
    }

    setShowLanding(false);

    const savedOnboarding=window.sessionStorage.getItem('dalepost_onboarding');

    if (passwordRecovery) {
      setScreen('auth');
      return;
    }

    if (savedOnboarding) {
      setFirstRunStage(savedOnboarding);
      return;
    }

    setScreen('create');
  },[
    authLoading,
    isAuthenticated,
    showLanding,
    passwordRecovery
  ]);

  useEffect(()=> {
    if (!needsFirstRunOnboarding || !isAuthenticated || passwordRecovery) {
      return;
    }

    window.sessionStorage.setItem('dalepost_onboarding','welcome');
    setShowLanding(false);
    setFirstRunStage('welcome');
    setScreen('create');
    setMenuOpen(false);
    clearNeedsFirstRunOnboarding();
  },[
    needsFirstRunOnboarding,
    isAuthenticated,
    passwordRecovery,
    clearNeedsFirstRunOnboarding
  ]);

  const refreshBrands=React.useCallback(async ()=> {
    if (!currentAccount?.id) {
      return;
    }

    setBrandsLoading(true);
    setBrandsError(null);

    const {data,error}=await api.brands.listBrands(currentAccount.id);

    if (error) {
      setBrandsError(error.message || 'Failed to load brands.');
      setBrands([]);
      setBrandsLoading(false);
      return;
    }

    const mapped=(data || []).map(mapDbBrandToLocal);
    setBrands(mapped);
    setBrandId((current)=> {
      if (mapped.some((item)=> item.id===current)) {
        return current;
      }
      return mapped[0]?.id || '';
    });
    setBrandsLoading(false);
  },[currentAccount?.id]);

  useEffect(()=> {
    if (!currentAccount?.id || !isAuthenticated || firstRunStage) {
      return;
    }

    refreshBrands();
  },[currentAccount?.id,isAuthenticated,firstRunStage,refreshBrands]);

  useEffect(()=> {
    if (!brandId || !isAuthenticated) {
      return;
    }

    let cancelled=false;

    const loadConnections=async ()=> {
      setConnectionsLoading(true);
      setConnectionsError(null);

      const {data,error}=await api.brandConnections.listConnections(brandId);

      if (cancelled) {
        return;
      }

      if (error) {
        setConnectionsError(error.message || 'Failed to load connections.');
      } else {
        setConnections((current)=> ({
          ...current,
          [brandId]:mapConnectionsToLocal(data)
        }));
      }

      setConnectionsLoading(false);
    };

    loadConnections();

    return ()=> {
      cancelled=true;
    };
  },[brandId,isAuthenticated]);

  useEffect(()=> {
    if (!brandId || !isAuthenticated) {
      return;
    }

    let cancelled=false;

    const loadAssets=async ()=> {
      setAssetsLoading(true);
      setAssetsError(null);

      const {data,error}=await api.brandAssets.listAssets(brandId);

      if (cancelled) {
        return;
      }

      if (error) {
        setAssetsError(error.message || 'Failed to load assets.');
      } else {
        setAssets((current)=> ({
          ...current,
          [brandId]:(data || []).map(mapDbAssetToLocal)
        }));
      }

      setAssetsLoading(false);
    };

    loadAssets();

    return ()=> {
      cancelled=true;
    };
  },[brandId,isAuthenticated]);

  const refreshLibraryPosts=React.useCallback(async ()=> {
    if (!brandId || !isAuthenticated) {
      return;
    }

    setLibraryPostsLoading(true);
    setLibraryPostsError(null);

    const {data,error}=await api.posts.listPosts(brandId);

    if (error) {
      setLibraryPostsError(error.message || 'Failed to load posts.');
      setLibraryPosts([]);
      setLibraryPostsLoading(false);
      return;
    }

    setLibraryPosts((data || []).map((row)=> api.posts.mapDbPostToLocal(row)));
    setLibraryPostsLoading(false);
  },[brandId,isAuthenticated]);

  useEffect(()=> {
    refreshLibraryPosts();
  },[refreshLibraryPosts]);

  const brand=brands.find((item)=> item.id===brandId)
    || brands[0]
    || fallbackBrand;

  const brandAssets=assets[brandId] || [];
  const brandConnections=connections[brandId]
    || createEmptyConnections();

  const enterAuth=()=> {
    setShowLanding(false);
    setScreen('auth');
    setMenuOpen(false);
  };

  const handleLoginSuccess=async ()=> {
    await refreshContext();
    window.sessionStorage.removeItem('dalepost_onboarding');
    setShowLanding(false);
    setFirstRunStage(null);
    setScreen('create');
    setMenuOpen(false);
  };

  const handleSignupSuccess=async ()=> {
    await refreshContext();
    window.sessionStorage.setItem('dalepost_onboarding','welcome');
    setShowLanding(false);
    setFirstRunStage('welcome');
    setScreen('create');
    setMenuOpen(false);
  };

  const handleSignOut=async ()=> {
    await api.signOut();
    window.sessionStorage.removeItem('dalepost_onboarding');
    setShowLanding(true);
    setScreen('auth');
    setFirstRunStage(null);
    setMenuOpen(false);
  };

  const navigate=(nextScreen)=> {
    if (!isAuthenticated) {
      setShowLanding(false);
      setScreen('auth');
      setMenuOpen(false);
      return;
    }

    setShowLanding(false);
    setScreen(nextScreen);
    setMenuOpen(false);

    if (nextScreen==='create') {
      setCurrentPost(null);
      setCreateError(null);
    }
  };

  const changeBrand=(id)=> {
    setBrandId(id);
    setScreen('create');
    setCurrentPost(null);
    setSelectedBackground(null);
    setMenuOpen(false);
  };

  const openBrandSetup=async (mode)=> {
    setReturnScreen(screen==='brand-setup' ? 'create' : screen);
    setBrandSetupMode(mode);

    if (mode==='edit' && brandId) {
      setSetupBrandLoading(true);
      const {data,error}=await api.brands.getBrand(brandId);
      setSetupBrandLoading(false);

      if (error) {
        setSetupBrand(brand);
      } else {
        setSetupBrand(mapDbBrandToLocal(data));
      }
    } else {
      setSetupBrand(null);
    }

    setScreen('brand-setup');
    setMenuOpen(false);
  };

  const saveBrand=async (values)=> {
    if (!currentAccount?.id) {
      return {error:'No account found. Please sign in again.'};
    }

    const accountId=currentAccount.id;

    if (brandSetupMode==='add') {
      const {data:created,error}=await api.brands.createBrand(
        accountId,
        mapLocalBrandToDb(values)
      );

      if (error) {
        return {error:error.message || 'Failed to create brand.'};
      }

      let logoUrl=created.logo_url;

      if (values.logoFile) {
        const logoResult=await syncBrandLogo(
          accountId,
          created.id,
          values.name,
          values.logoFile
        );

        if (logoResult.error) {
          return {
            error:logoResult.error.message || 'Brand created, but logo upload failed.'
          };
        }

        logoUrl=logoResult.logoUrl;

        const {error:updateError}=await api.brands.updateBrand(created.id,{
          logo_url:logoUrl
        });

        if (updateError) {
          return {
            error:updateError.message || 'Brand created, but logo could not be saved.'
          };
        }
      }

      await refreshBrands();
      setBrandId(created.id);
      setCurrentPost(null);
      setSelectedBackground(null);
      setScreen('create');
      return {error:null};
    }

    let logoUrl=values.logoFile
      ? brand.logo
      : (values.logo?.startsWith('blob:') ? brand.logo : values.logo);

    if (values.logoFile) {
      const logoResult=await syncBrandLogo(
        accountId,
        brandId,
        values.name,
        values.logoFile
      );

      if (logoResult.error) {
        return {error:logoResult.error.message || 'Failed to upload logo.'};
      }

      logoUrl=logoResult.logoUrl;
    }

    const {error}=await api.brands.updateBrand(
      brandId,
      mapLocalBrandToDb({...values,logo:logoUrl})
    );

    if (error) {
      return {error:error.message || 'Failed to update brand.'};
    }

    await refreshBrands();

    const {data:assetRows}=await api.brandAssets.listAssets(brandId);
    setAssets((current)=> ({
      ...current,
      [brandId]:(assetRows || []).map(mapDbAssetToLocal)
    }));

    setScreen(returnScreen==='brand-setup' ? 'create' : returnScreen);
    return {error:null};
  };

  const saveFirstBrand=async (values)=> {
    if (!currentAccount?.id) {
      return {error:'Cannot create brand without an account.'};
    }

    const accountId=currentAccount.id;
    const {data:created,error}=await api.brands.createBrand(
      accountId,
      mapLocalBrandToDb(values)
    );

    if (error) {
      return {error:error.message || 'Failed to create brand.'};
    }

    let logoUrl=created.logo_url;

    if (values.logoFile) {
      const logoResult=await syncBrandLogo(
        accountId,
        created.id,
        values.name,
        values.logoFile
      );

      if (logoResult.error) {
        return {
          error:logoResult.error.message || 'Brand created, but logo upload failed.'
        };
      }

      logoUrl=logoResult.logoUrl;

      const {error:updateError}=await api.brands.updateBrand(created.id,{
        logo_url:logoUrl
      });

      if (updateError) {
        return {
          error:updateError.message || 'Brand created, but logo could not be saved.'
        };
      }
    }

    const newBrand=mapDbBrandToLocal({...created,logo_url:logoUrl});

    setBrands([newBrand]);
    setBrandId(newBrand.id);
    setConnections({[newBrand.id]:createEmptyConnections()});

    const {data:assetRows}=await api.brandAssets.listAssets(newBrand.id);
    setAssets({
      [newBrand.id]:(assetRows || []).map(mapDbAssetToLocal)
    });
    setFirstRunStage('complete');
    window.sessionStorage.setItem('dalepost_onboarding','complete');
    return {error:null};
  };

  const finishFirstRun=()=> {
    window.sessionStorage.removeItem('dalepost_onboarding');
    setFirstRunStage(null);
    setCurrentPost(null);
    setSelectedBackground(null);
    setScreen('create');
  };

  const cancelBrandSetup=()=> {
    setScreen(returnScreen==='brand-setup' ? 'create' : returnScreen);
  };

  const updateConnection=async (platform,value)=> {
    setConnectionsError(null);

    if (value.connected) {
      const {data,error}=await api.brandConnections.connectPlatform(
        brandId,
        platform,
        value.handle
      );

      if (error) {
        setConnectionsError(error.message || 'Failed to connect platform.');
        return;
      }

      setConnections((current)=> ({
        ...current,
        [brandId]:{
          ...(current[brandId] || createEmptyConnections()),
          [platform]:{
            connected:true,
            handle:data.handle,
            id:data.id
          }
        }
      }));
      return;
    }

    const connectionId=connections[brandId]?.[platform]?.id;

    if (!connectionId) {
      return;
    }

    const {error}=await api.brandConnections.disconnectPlatform(connectionId);

    if (error) {
      setConnectionsError(error.message || 'Failed to disconnect platform.');
      return;
    }

    setConnections((current)=> ({
      ...current,
      [brandId]:{
        ...(current[brandId] || createEmptyConnections()),
        [platform]:{connected:false,handle:null,id:null}
      }
    }));
  };

  const inviteTeammate=(member)=> {
    setTeamMembers((current)=> [
      ...current,
      {
        ...member,
        id:`invite-${Date.now()}`
      }
    ]);
  };

  const cancelInvite=(memberId)=> {
    setTeamMembers((current)=> current.filter(
      (member)=> (
        member.id!==memberId || member.status==='active'
      )
    ));
  };

  const addManualAsset=async (file)=> {
    if (!currentAccount?.id) {
      setAssetsError('No account found. Please sign in again.');
      return;
    }

    setAssetsError(null);

    const assetId=crypto.randomUUID();
    const {data:uploadData,error:uploadError}=await api.brandAssets.uploadBrandAssetFile(
      currentAccount.id,
      brandId,
      assetId,
      file
    );

    if (uploadError) {
      setAssetsError(uploadError.message || 'Failed to upload asset.');
      return;
    }

    const {data,error}=await api.brandAssets.addAsset(brandId,{
      storage_path:uploadData.storagePath,
      label:file.name,
      source:'manual'
    });

    if (error) {
      setAssetsError(error.message || 'Failed to save asset.');
      return;
    }

    const asset=mapDbAssetToLocal(data);
    setAssets((current)=> ({
      ...current,
      [brandId]:[asset,...(current[brandId] || [])]
    }));
  };

  const removeAsset=async (assetId)=> {
    setAssetsError(null);

    const {error}=await api.brandAssets.removeAsset(assetId);

    if (error) {
      setAssetsError(error.message || 'Failed to remove asset.');
      return;
    }

    setAssets((current)=> ({
      ...current,
      [brandId]:(current[brandId] || []).filter(
        (asset)=> asset.id!==assetId
      )
    }));

    if (selectedBackground?.id===assetId) {
      setSelectedBackground(null);
    }
  };

  const useAsset=(asset)=> {
    setSelectedBackground(asset);
    setCurrentPost(null);
    setScreen('create');
    setMenuOpen(false);
  };

  const generateWithMockTemplates=async ({
    prompt,
    seedContent,
    inputMode,
    backgroundImage,
    screenshotFile
  })=> {
    setGenerating(true);
    setCreateError(null);

    await new Promise((resolve)=> window.setTimeout(resolve,1350));

    try {
      const template=postTemplates[
        Math.floor(Math.random()*postTemplates.length)
      ];
      const layout=selectRandomLayout(brand.preferredLayouts);
      const postId=crypto.randomUUID();
      let imageStoragePath=backgroundImage || template.image || null;
      let imageUrl=backgroundImage || template.image;

      if (inputMode==='screenshot' && screenshotFile) {
        const uploadResult=await api.posts.uploadPostScreenshot(
          currentAccount.id,
          brandId,
          postId,
          screenshotFile
        );

        if (uploadResult.error) {
          throw uploadResult.error;
        }

        imageStoragePath=uploadResult.data.storagePath;
        imageUrl=uploadResult.data.publicUrl;
      } else if (selectedBackground?.storagePath) {
        imageStoragePath=selectedBackground.storagePath;
        imageUrl=selectedBackground.imageUrl;
      }

      const storedPrompt=inputMode==='screenshot'
        ? prompt
        : (seedContent || prompt);

      const {data:created,error}=await api.posts.createPost(brandId,{
        id:postId,
        prompt:storedPrompt,
        seed_type:inputMode,
        headline:template.headline,
        subhead:template.supporting,
        image_storage_path:imageStoragePath,
        layout_id:layout,
        status:'draft'
      });

      if (error) {
        throw error;
      }

      const captionEntries=Object.entries(template.caption || {});

      for (const [platform,caption] of captionEntries) {
        const {error:captionError}=await api.postCaptions.upsertCaption(
          created.id,
          platform,
          caption
        );

        if (captionError) {
          throw captionError;
        }
      }

      const newPost=api.posts.mapDbPostToLocal(created,template.caption);

      setLibraryPosts((items)=> [newPost,...items]);
      setCurrentPost({
        ...newPost,
        image:imageUrl || newPost.image
      });
      setSelectedBackground(null);
      setScreen('review');
    } catch (error) {
      setCreateError(
        error?.message || 'Failed to create post. Please try again.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const generate=async ({
    prompt,
    seedContent,
    inputMode,
    backgroundImage,
    screenshotFile
  })=> {
    if (!currentAccount?.id || !brandId) {
      setCreateError('No account or brand selected. Please try again.');
      return;
    }

    if (USE_MOCK_GENERATION) {
      return generateWithMockTemplates({
        prompt,
        seedContent,
        inputMode,
        backgroundImage,
        screenshotFile
      });
    }

    setGenerating(true);
    setCreateError(null);

    const postId=crypto.randomUUID();
    const storedPrompt=inputMode==='screenshot'
      ? prompt
      : (seedContent || prompt);
    const generationSeed=inputMode==='screenshot'
      ? storedPrompt
      : (seedContent || prompt);

    try {
      const layout=selectRandomLayout(brand.preferredLayouts);

      const {data:created,error:createError}=await api.posts.createPost(brandId,{
        id:postId,
        prompt:storedPrompt,
        seed_type:inputMode,
        layout_id:layout,
        status:'draft'
      });

      if (createError) {
        throw createError;
      }

      const {data:generated,error:generateError}=await api.posts.generatePost({
        brandId,
        seedType:inputMode,
        seedContent:generationSeed,
        postId:created.id
      });

      if (generateError) {
        throw generateError;
      }

      const {error:updateError}=await api.posts.updatePost(postId,{
        headline:generated.headline,
        subhead:generated.subhead
      });

      if (updateError) {
        throw updateError;
      }

      const captionEntries=Object.entries(generated.captions || {});

      for (const [platform,caption] of captionEntries) {
        const {error:captionError}=await api.postCaptions.upsertCaption(
          postId,
          platform,
          caption
        );

        if (captionError) {
          throw captionError;
        }
      }

      const {data:completePost,error:fetchError}=await api.posts.getPost(postId);

      if (fetchError) {
        throw fetchError;
      }

      const {data:captionRows,error:captionsError}=await api.postCaptions.getCaptions(
        postId
      );

      if (captionsError) {
        throw captionsError;
      }

      const newPost=api.posts.mapDbPostToLocal(completePost,captionRows || []);

      setLibraryPosts((items)=> [newPost,...items]);
      setCurrentPost(newPost);
      setSelectedBackground(null);
      setScreen('review');
    } catch (error) {
      setCreateError(
        error?.message || 'Failed to generate post. Please try again.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus=(
    status,
    rejectionReason='',
    revisedPost=currentPost
  )=> {
    const updatedPost={
      ...currentPost,
      ...revisedPost,
      status,
      rejectionReason
    };

    setLibraryPosts((items)=> items.map((item)=> (
      item.id===currentPost.id ? updatedPost : item
    )));
    setPosts((items)=> items.map((post)=> (
      post.id===currentPost.id ? updatedPost : post
    )));
    setScreen('library');
    setCurrentPost(null);
  };

  const publishPost=(postId,publishedPlatforms)=> {
    setPosts((items)=> items.map((post)=> (
      post.id===postId
        ? {
          ...post,
          status:'posted',
          publishedPlatforms,
          engagement:createMockEngagement(
            post.id,
            publishedPlatforms.length
          ),
          postedDate:new Date().toISOString().slice(0,10)
        }
        : post
    )));
  };

  const openPost=async (post)=> {
    const isPersistedPost=typeof post.id==='string' && post.id.includes('-');

    if (isPersistedPost) {
      const {data:captionRows}=await api.postCaptions.getCaptions(post.id);

      if (captionRows?.length) {
        setCurrentPost({
          ...post,
          caption:Object.fromEntries(
            captionRows.map((row)=> [row.platform,row.caption])
          )
        });
        setScreen('review');
        setShowLanding(false);
        return;
      }
    }

    setCurrentPost(post);
    setScreen('review');
    setShowLanding(false);
  };

  const schedulePost=async (postId,scheduledDate)=> {
    const {data,error}=await api.posts.schedulePost(postId,scheduledDate);

    if (error) {
      return {error};
    }

    const updatedPost=api.posts.mapDbPostToLocal(data);

    setLibraryPosts((items)=> items.map((post)=> (
      post.id===postId ? {...post,...updatedPost} : post
    )));

    return {error:null};
  };

  if (authLoading) {
    return (
      <div className="app-shell auth-loading">
        <p>Loading workspace…</p>
      </div>
    );
  }

  const isPublicScreen=showLanding
    || screen==='auth'
    || Boolean(firstRunStage)
    || passwordRecovery;

  if (!isAuthenticated && !isPublicScreen) {
    return (
      <div
        className="app-shell"
        style={{'--brand':brand.primary,'--accent':brand.accent}}
      >
        <AuthScreen
          brand={brand}
          onLoginSuccess={handleLoginSuccess}
          onSignupSuccess={handleSignupSuccess}
          onRecoveryComplete={handleLoginSuccess}
        />
      </div>
    );
  }

  if (showLanding) {
    return (
      <div
        className="app-shell"
        style={{'--brand':brand.primary,'--accent':brand.accent}}
      >
        <LandingPage brand={brand} onEnterApp={enterAuth} />
      </div>
    );
  }

  if ((screen==='auth' || passwordRecovery) && !firstRunStage) {
    return (
      <div
        className="app-shell"
        style={{'--brand':brand.primary,'--accent':brand.accent}}
      >
        <AuthScreen
          brand={brand}
          onLoginSuccess={handleLoginSuccess}
          onSignupSuccess={handleSignupSuccess}
          onRecoveryComplete={handleLoginSuccess}
        />
      </div>
    );
  }

  if (firstRunStage) {
    return (
      <div
        className="app-shell"
        style={{'--brand':brand.primary,'--accent':brand.accent}}
      >
        <FirstRunOnboarding
          stage={firstRunStage}
          brand={brands[0]}
          onBegin={()=> {
            setFirstRunStage('setup');
            window.sessionStorage.setItem('dalepost_onboarding','setup');
          }}
          onSaveBrand={saveFirstBrand}
          onBack={()=> {
            setFirstRunStage('welcome');
            window.sessionStorage.setItem('dalepost_onboarding','welcome');
          }}
          onCreatePost={finishFirstRun}
        />
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      style={{'--brand':brand.primary,'--accent':brand.accent}}
    >
      <button
        className="mobile-menu"
        onClick={()=> setMenuOpen(!menuOpen)}
      >
        <SafeIcon icon={menuOpen ? FiX : FiMenu} />
        <span>DalePost</span>
      </button>

      <div className={`sidebar-wrap ${menuOpen ? 'open' : ''}`}>
        <Sidebar
          brands={brands}
          brandsLoading={brandsLoading}
          brandsError={brandsError}
          activeBrand={brand}
          screen={screen}
          user={currentUser}
          role={currentRole}
          onBrandChange={changeBrand}
          onNavigate={navigate}
          onAddBrand={()=> openBrandSetup('add')}
          onEditBrand={()=> openBrandSetup('edit')}
          onSignOut={handleSignOut}
        />
      </div>

      {screen==='brand-setup' && (
        <BrandSetup
          key={`${brandSetupMode}-${brandId}-${setupBrand?.id || 'new'}`}
          brand={brandSetupMode==='edit' ? (setupBrand || brand) : null}
          mode={brandSetupMode}
          loading={setupBrandLoading}
          onSave={saveBrand}
          onCancel={cancelBrandSetup}
        />
      )}

      {screen==='connections' && (
        <ConnectionsScreen
          brand={brand}
          connections={brandConnections}
          loading={connectionsLoading}
          error={connectionsError}
          onChange={updateConnection}
        />
      )}

      {screen==='team' && (
        <TeamScreen
          brand={brand}
          members={teamMembers}
          onInvite={inviteTeammate}
          onCancelInvite={cancelInvite}
        />
      )}

      {screen==='billing' && (
        <BillingScreen
          currentPlan={currentPlan}
          brandCount={brands.length}
          postCount={posts.length}
          onChangePlan={setCurrentPlan}
        />
      )}

      {screen==='assets' && (
        <AssetLibraryScreen
          brand={brand}
          assets={brandAssets}
          loading={assetsLoading}
          error={assetsError}
          onAdd={addManualAsset}
          onRemove={removeAsset}
          onUse={useAsset}
        />
      )}

      {screen==='create' && (
        <CreateScreen
          brand={brand}
          generating={generating}
          error={createError}
          selectedBackground={selectedBackground}
          onClearBackground={()=> setSelectedBackground(null)}
          onGenerate={generate}
        />
      )}

      {screen==='review' && currentPost && (
        <ReviewScreen
          post={currentPost}
          brand={brand}
          accountId={currentAccount?.id}
          brandId={brandId}
          onAssetUploaded={(asset)=> {
            setAssets((current)=> ({
              ...current,
              [brandId]:[asset,...(current[brandId] || [])]
            }));
          }}
          onApprove={(revisedPost)=> (
            updateStatus('approved','',revisedPost)
          )}
          onReject={(reason,revisedPost)=> (
            updateStatus('rejected',reason,revisedPost)
          )}
        />
      )}

      {screen==='library' && (
        <LibraryScreen
          posts={libraryPosts}
          brand={brand}
          loading={libraryPostsLoading}
          error={libraryPostsError}
          connections={brandConnections}
          onCreate={()=> navigate('create')}
          onOpen={openPost}
          onPublish={publishPost}
          onOpenConnections={()=> navigate('connections')}
        />
      )}

      {screen==='calendar' && (
        <CalendarScreen
          posts={libraryPosts}
          brand={brand}
          loading={libraryPostsLoading}
          error={libraryPostsError}
          onOpen={openPost}
          onSchedule={schedulePost}
        />
      )}

      {screen==='learnings' && (
        <LearningsScreen
          posts={libraryPosts}
          brand={brand}
          loading={libraryPostsLoading}
          error={libraryPostsError}
          onOpen={openPost}
        />
      )}
    </div>
  );
}

export default App;